import _ from 'lodash';
import { AppInfoLoader } from '@services/AppInfoLoader';
import { UUIDUtils } from '@utils/UuidUtils';
import { EnvironmentDetail } from '@utils/EnvironmentDetailUtils';
import { CountMetric } from '@src/metrics/CountMetric';
import '@amzn/ihm-the-truth-machine/validator';
import { IhmLaborTrackingServiceClient } from '@services/coralservices/serviceClients/IhmLaborTrackingServiceClient';
import { WeblabService } from '@services/WeblabService';

const APPLICATION_NAME = 'IhmFlyingDutchman';
const OPERATION_NAME = 'AssociateAuditTrail.recordEvent';

export interface IBaseAuditEvent {
    Process: string;
    RequestId: string;
    UserAgent?: string;
}

export interface IBaseAuditEventWithZoneInfo extends IBaseAuditEvent {
    Zone: string;
    Subzone: string;
}

export interface IValidationResult {
    valid: boolean;
    error?: string;
}

export interface IAssociateAuditTrailEventInvalidException {
    message: string;
    validationResult: IValidationResult;
}

export class AssociateAuditTrailService {
    public static generateBaseEvent(process: string): IBaseAuditEvent {
        return {
            Process: process,
            RequestId: UUIDUtils.generate(),
            UserAgent: EnvironmentDetail.getDeviceType(),
        };
    }

    public static async generateBaseEventWithZoneInfo(process: string): Promise<IBaseAuditEventWithZoneInfo> {
        const appConfig = await AppInfoLoader.getAppConfig();
        return {
            ...this.generateBaseEvent(process),
            Zone: appConfig.zoneInfo.zoneName,
            Subzone: appConfig.zoneInfo.subzoneName,
        };
    }

    public static validateMessage<T extends IBaseAuditEvent>(eventMessage: T): IValidationResult {
        const ihmTheTruthMachineJSClient = (window as any).ihmTheTruthMachineJSClient;

        if (!ihmTheTruthMachineJSClient) {
            return { valid: true };
        }

        const eventForValidation = _.extend(
            {
                StoreId: 'placeholder-store-id',
                User: 'placeholder-user',
                Timestamp: 0,
            },
            eventMessage,
        );
        return ihmTheTruthMachineJSClient.validateMessage(eventForValidation);
    }

    public static async recordEventAndEmitMetric<T extends IBaseAuditEvent>(storeId: string, eventMessage: T, eventType: string) {
        try {
            await this.recordEvent(storeId, eventMessage);
            console.debug(`ATBase Recorded event of type: ${eventType}.`);
            const metric = new CountMetric(storeId, `${eventType}.Success.Count`, APPLICATION_NAME, OPERATION_NAME);
            metric.count(1);
        } catch (error) {
            console.error(`ATBase Failed to record event of type: ${eventType}. Error: ${JSON.stringify(error)}`);
            const metric = new CountMetric(storeId, `${eventType}.Error.Count`, APPLICATION_NAME, OPERATION_NAME);
            metric.count(1);
            throw error;
        } finally {
            const metric = new CountMetric(storeId, `${eventType}.Total.Count`, APPLICATION_NAME, OPERATION_NAME);
            metric.count(1);
        }
    }

    private static async recordEvent<T extends IBaseAuditEvent>(storeId: string, eventMessage: T): Promise<void> {
        const validationResult = this.validateMessage(eventMessage);

        if (validationResult.valid) {
            const eventJsonString = JSON.stringify(eventMessage);
            try {
                await IhmLaborTrackingServiceClient.recordAssociateAudit(storeId, eventJsonString);
                console.debug(`ATBase Sent audit event to LTS: storeId = ${storeId}, message = ${eventJsonString}`);
            } catch (error) {
                console.error(`ATBase LTS failed to record audit event: ${eventJsonString}, Error message: ${JSON.stringify(error)}`);
                throw error;
            }
        } else {
            throw {
                message: `Invalid audit trail event. Error: ${validationResult.error}`,
                validationResult,
            };
        }
    }
}
